import React, { useEffect, useMemo, useState } from "react";
import {
  Select,
  MenuItem,
  Box,
  ListSubheader,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { COMPOSER_MOBILE_MEDIA_QUERY } from "./composerConstants";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useChannels } from "../../hooks/useChannels";
import { getChannelIcon } from "../../utils/utils";
import { fetchData } from "../../api/client";
import { workspaceScopedQueryKey } from "../../utils/workspaceQueryScope";
import {
  canUseClientChannelForConversation,
  getConversationOriginChannelId,
} from "../../utils/clientContactUtils";
import { deriveConversationContext } from "../../utils/conversationContextUtils";
import { isActiveChannel, isComposerOutboundChannel } from "../../constants/channelCatalog";
import { isWhatsappGroupConversation } from "../../utils/whatsappQrGroupMentions";
import { useAuthContext } from "../../context/AuthContext";
import { useEffectivePermissions } from "../../hooks/useEffectivePermissions";
import { isOutboundChannelIdBlocked } from "../../utils/cargoChannelSendBlocks";

export const INTERNAL_NOTE_CHANNEL_ID = "internal_note";
export const PERSONAL_WHATSAPP_CHANNEL_ID = "personal_whatsapp";

const PINNED_KEY = "xpro.chat.pinnedChannels";

const getChannelGroup = (channelType, label) => {
  const t = String(channelType || "").toLowerCase();
  const name = String(label || "").toLowerCase();
  if (t.includes("whatsapp") || name.includes("whatsapp")) return "WhatsApp";
  if (t === "email" || name.includes("email") || name.includes("e-mail")) return "Email";
  if (t === "phone" || t === "sms" || name.includes("telefone") || name.includes("sms")) return "Telefone e SMS";
  if (t === "messenger" || t === "instagram" || t === "facebook" || t.includes("comentário") || t.includes("comentarios")) return "Redes sociais";
  if (t === "portal cliente" || name.includes("portal") || name.includes("xpro cliente")) return "Portal do cliente";
  if (t.includes("chat no site") || name.includes("chat no site")) return "Apps e web";
  if (t === "app" || t === "wordpress" || t === "ecommerce" || t === "website") return "Apps e web";
  return "Outros canais";
};

const getChannelHint = (t, isOrigin, isLastClientContact) => {
  if (isOrigin && isLastClientContact) {
    return t("compose.channelOriginAndLastContact", "Origem e último contato");
  }
  if (isOrigin) return t("compose.channelOrigin", "Origem da solicitação");
  if (isLastClientContact) {
    return t("compose.channelLastContact", "Último contato do cliente");
  }
  return null;
};

const readPinned = () => {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const ChannelSelector = ({
  selectedChannel,
  setSelectedChannel,
  integrated = false,
  personalWhatsappComposerOnly = false,
  clientModeOnly = false,
  clientConversation = null,
  conversationMessages = null,
}) => {
  const { t } = useTranslation("chat");
  const isMobile = useMediaQuery(COMPOSER_MOBILE_MEDIA_QUERY);
  const { userData } = useAuthContext();
  const { data: permData } = useEffectivePermissions();
  const { data: channelsData } = useChannels();
  /** Enquanto o catálogo não chegou, options só tem anotação — não “corrigir” seleção. */
  const channelsCatalogReady = channelsData != null;
  const [search, setSearch] = useState("");
  const pinnedIds = useMemo(() => readPinned(), []);

  const { data: meRoot } = useQuery(workspaceScopedQueryKey("me"), () => fetchData("/users/me"), {
    staleTime: 60000,
  });
  const wp = meRoot?.data?.whatsapp_personal;
  const showPersonalWhatsapp =
    !personalWhatsappComposerOnly &&
    Boolean(wp?.enabled) &&
    String(wp?.status || "") === "connected";

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (value === 0 || value === "0") return "";
    return value;
  };

  /** Canal em que a solicitação foi aberta. */
  const originChannelId = useMemo(
    () => getConversationOriginChannelId(clientConversation),
    [clientConversation]
  );

  /** Canal da última mensagem do cliente — é por ele que a resposta chega. */
  const lastClientChannelId = useMemo(() => {
    if (!Array.isArray(conversationMessages) || !conversationMessages.length) return null;
    const id = deriveConversationContext(conversationMessages).lastClientMessage
      ?.messageChannelId;
    return id != null && id !== "" && Number(id) > 0 ? id : null;
  }, [conversationMessages]);

  const channelOptions = useMemo(() => {
    if (personalWhatsappComposerOnly) {
      return [
        {
          value: PERSONAL_WHATSAPP_CHANNEL_ID,
          label: "WhatsApp pessoal",
          icon: <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />,
          group: "WhatsApp",
        },
      ];
    }
    const fromApi = (channelsData?.channels ?? [])
      .filter(
        (channel) =>
          isComposerOutboundChannel(channel) &&
          isActiveChannel(channel) &&
          !isOutboundChannelIdBlocked(channel.id, permData?.blockedOutboundChannelIds, {
            isMaster: Number(userData?.master) === 1,
          }) &&
          (!clientConversation ||
            canUseClientChannelForConversation(
              clientConversation,
              channel.id,
              channel.channelType,
              channel.tipo,
              conversationMessages
            ))
      )
      .map((channel) => {
        const isOrigin =
          originChannelId != null && String(channel.id) === String(originChannelId);
        const isLastClientContact =
          lastClientChannelId != null &&
          String(channel.id) === String(lastClientChannelId);
        return {
          value: channel.id,
          label: channel.nome,
          icon: getChannelIcon(channel.nome, channel.channelType, channel.tipo),
          group: getChannelGroup(channel.channelType, channel.nome),
          channelType: channel.channelType,
          isOrigin,
          isLastClientContact,
          hint: getChannelHint(t, isOrigin, isLastClientContact),
        };
      });
    const personalOpt =
      showPersonalWhatsapp &&
      !isWhatsappGroupConversation(clientConversation, conversationMessages)
        ? [
            {
              value: PERSONAL_WHATSAPP_CHANNEL_ID,
              label: "WhatsApp pessoal",
              icon: <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />,
              group: "WhatsApp",
            },
          ]
        : [];
    const selectedIsInternalNote =
      String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID);
    // Em modo cliente o menu omite anotação; se ela já está selecionada (ex.: remount
    // antes do composeMode sincronizar), mantém a opção para não disparar auto-correct.
    const internalOpt =
      clientModeOnly && !selectedIsInternalNote
        ? []
        : [
            {
              value: INTERNAL_NOTE_CHANNEL_ID,
              label: "Anotações internas",
              icon: <EditNoteIcon fontSize="small" />,
              group: "Interno",
            },
          ];
    // Canais da solicitação primeiro: com vários canais do mesmo tipo (ex.: dois
    // WhatsApp), achar o canal certo à mão é o que mais confunde no atendimento.
    const conversationFirst = [
      ...fromApi.filter((opt) => opt.isOrigin || opt.isLastClientContact),
      ...fromApi.filter((opt) => !opt.isOrigin && !opt.isLastClientContact),
    ];
    return [...internalOpt, ...personalOpt, ...conversationFirst];
  }, [
    channelsData,
    showPersonalWhatsapp,
    personalWhatsappComposerOnly,
    clientModeOnly,
    selectedChannel,
    clientConversation,
    conversationMessages,
    originChannelId,
    lastClientChannelId,
    t,
    permData?.blockedOutboundChannelIds,
    userData?.master,
  ]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return channelOptions;
    return channelOptions.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [channelOptions, search]);

  const groupedMenu = useMemo(() => {
    const pinned = filteredOptions.filter((o) => pinnedIds.includes(String(o.value)));
    const byGroup = {};
    filteredOptions.forEach((opt) => {
      if (pinnedIds.includes(String(opt.value))) return;
      const g = opt.group || "Outros";
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push(opt);
    });
    return { pinned, byGroup };
  }, [filteredOptions, pinnedIds]);

  const normalizedSelectedChannel = normalizeValue(selectedChannel);
  /** Anotação / WhatsApp pessoal: nunca “corrigir” para outro canal (race no remount). */
  const isProtectedVirtualChannel =
    String(normalizedSelectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) ||
    (String(normalizedSelectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID) &&
      !isWhatsappGroupConversation(clientConversation, conversationMessages));
  const valueInOptions = channelOptions.some(
    (opt) =>
      String(opt.value) === String(normalizedSelectedChannel) ||
      (opt.value !== INTERNAL_NOTE_CHANNEL_ID &&
        opt.value !== PERSONAL_WHATSAPP_CHANNEL_ID &&
        parseInt(opt.value) === parseInt(normalizedSelectedChannel))
  );
  const safeSelectValue = isProtectedVirtualChannel
    ? normalizedSelectedChannel
    : channelOptions?.length && valueInOptions
      ? normalizedSelectedChannel
      : channelOptions?.[0]?.value ?? "";

  // Se o canal selecionado deixou de ser usável (ex.: Facebook sem publicação),
  // corrige o estado para a primeira opção disponível.
  // Não aplica a virtual channels (anotação / WA pessoal) — o 1º render com
  // clientModeOnly pode ocultar “Anotações internas” e sobrescrever a preferência.
  // Também não aplica enquanto o catálogo de canais ainda não carregou: senão a
  // lista temporária [internal_note] grava anotação na sessão e prende o composer.
  useEffect(() => {
    if (!channelsCatalogReady) return;
    if (isProtectedVirtualChannel) return;
    if (!channelOptions.length || valueInOptions) return;
    if (safeSelectValue === "" || safeSelectValue == null) return;
    if (String(safeSelectValue) === String(normalizedSelectedChannel)) return;
    if (typeof setSelectedChannel === "function") {
      setSelectedChannel(safeSelectValue);
    }
  }, [
    channelsCatalogReady,
    isProtectedVirtualChannel,
    channelOptions.length,
    valueInOptions,
    safeSelectValue,
    normalizedSelectedChannel,
    setSelectedChannel,
  ]);

  const selectedOption = useMemo(
    () =>
      channelOptions?.find(
        (opt) =>
          safeSelectValue !== "" &&
          (String(opt.value) === String(safeSelectValue) ||
            (opt.value !== INTERNAL_NOTE_CHANNEL_ID &&
              opt.value !== PERSONAL_WHATSAPP_CHANNEL_ID &&
              parseInt(opt.value) === parseInt(safeSelectValue)))
      ) ?? null,
    [channelOptions, safeSelectValue]
  );

  /** Ícones se repetem entre canais do mesmo tipo — aí o nome precisa aparecer. */
  const showSelectedLabel = useMemo(() => {
    if (!integrated || !selectedOption?.label) return false;
    if (isMobile) return false;
    if (selectedOption.value === INTERNAL_NOTE_CHANNEL_ID) return false;
    return (
      channelOptions.filter((opt) => opt.group === selectedOption.group).length > 1
    );
  }, [integrated, selectedOption, channelOptions, isMobile]);

  const renderOptionContent = (option) => (
    <>
      {option.icon}
      <Box sx={{ marginLeft: 1, minWidth: 0 }}>
        <Box>{option.label}</Box>
        {option.hint ? (
          <Typography variant="caption" color="text.secondary" component="div">
            {option.hint}
          </Typography>
        ) : null}
      </Box>
    </>
  );

  const renderMenuItems = () => {
    const items = [];
    if (!integrated && filteredOptions.length > 5) {
      items.push(
        <ListSubheader key="search" sx={{ lineHeight: 1, py: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t("compose.searchChannel", "Buscar canal…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </ListSubheader>
      );
    }
    if (groupedMenu.pinned.length) {
      items.push(
        <ListSubheader key="pinned">{t("compose.pinnedChannels", "Fixados")}</ListSubheader>
      );
      groupedMenu.pinned.forEach((option) => {
        items.push(
          <MenuItem key={`pin-${option.value}`} value={option.value}>
            {renderOptionContent(option)}
          </MenuItem>
        );
      });
    }
    Object.entries(groupedMenu.byGroup).forEach(([group, opts]) => {
      items.push(<ListSubheader key={group}>{group}</ListSubheader>);
      opts.forEach((option) => {
        items.push(
          <MenuItem key={option.value} value={option.value}>
            {renderOptionContent(option)}
          </MenuItem>
        );
      });
    });
    return items;
  };

  return (
    <Select
      value={safeSelectValue}
      onChange={(e) => setSelectedChannel(e.target.value)}
      size="small"
      MenuProps={{
        disableScrollLock: true,
        disablePortal: false,
        container: typeof document !== "undefined" ? document.body : undefined,
        sx: { zIndex: 200001 },
        PaperProps: { sx: { zIndex: 200002, maxHeight: 360 } },
      }}
      sx={{
        minWidth: integrated ? (showSelectedLabel ? 88 : isMobile ? 36 : 40) : "32px",
        width: integrated ? (showSelectedLabel ? "auto" : isMobile ? 36 : 44) : "40px",
        maxWidth: showSelectedLabel ? { xs: 132, sm: 176 } : undefined,
        padding: 0,
        bgcolor: integrated ? "transparent" : undefined,
        borderRadius: integrated ? "10px" : undefined,
        transition: integrated ? "background-color 0.15s ease" : undefined,
        "&:hover": integrated ? { bgcolor: "action.hover" } : undefined,
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-select": {
          display: "flex",
          justifyContent: showSelectedLabel ? "flex-start" : "center",
          alignItems: "center",
          gap: showSelectedLabel ? 0.5 : 0,
          overflow: "hidden",
          padding: integrated
            ? showSelectedLabel
              ? "6px 8px !important"
              : isMobile
                ? "4px 2px !important"
                : "6px 4px !important"
            : "0 !important",
          margin: 0,
          minHeight: integrated ? (isMobile ? 36 : 40) : undefined,
        },
        "& .MuiSelect-icon": { display: "none" },
      }}
      renderValue={() => {
        const content = (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: showSelectedLabel ? 0.5 : 0,
              minWidth: 0,
            }}
          >
            {selectedOption?.icon || getChannelIcon("default", "default")}
            {showSelectedLabel ? (
              <Typography variant="caption" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
                {selectedOption.label}
              </Typography>
            ) : null}
          </Box>
        );
        if (!selectedOption?.label) return content;
        return (
          <Tooltip
            title={
              selectedOption.hint
                ? `${selectedOption.label} · ${selectedOption.hint}`
                : selectedOption.label
            }
          >
            {content}
          </Tooltip>
        );
      }}
    >
      {integrated
        ? channelOptions?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {renderOptionContent(option)}
            </MenuItem>
          ))
        : renderMenuItems()}
    </Select>
  );
};

export default ChannelSelector;
